import { useCallback, useMemo } from 'react';
import { generatePath, useMatch, useNavigate, useResolvedPath } from 'react-router-dom';

import { PersonReadModel } from '@client/api';
import { isDefinedString, isNotNullish, mask, TableCell, TableRow, Text, toCountry } from '@client/toolkit';

import { UsersPaths } from '../../users.router';

type UserListRowProps = {
  user?: PersonReadModel;
  isFetching?: boolean;
}

export function UserListRow({ user, isFetching }: UserListRowProps) {
  const userPath = useResolvedPath(UsersPaths.User);
  const userPathMatch = useMatch<'id', string>({ path: userPath.pathname });
  const selectedUserId = useMemo(() => (isNotNullish(userPathMatch) ? userPathMatch.params.id : null), [userPathMatch]);

  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!user?.id) return;

    if (user.id === selectedUserId) return navigate(generatePath(UsersPaths.Users));

    navigate(generatePath(UsersPaths.User, { id: user.id }));
  }, [navigate, user?.id, selectedUserId]);

  const country = useMemo(() => toCountry(user?.socialSecurityNumber?.country?.threeLetterCode)?.name, [user?.socialSecurityNumber?.country?.threeLetterCode])

  return (
    <TableRow hover={user?.id != null} selected={selectedUserId === user?.id} onClick={handleClick}>
      <TableCell loading={isFetching}>
        <Text className="font-semibold" isEmpty={!isDefinedString(user?.name)}>
          {user?.name}
        </Text>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(user?.email)}>
          {user?.email}
        </Text>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(country)}>
          {country}
        </Text>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(user?.socialSecurityNumber?.value)}>
          {mask(user?.socialSecurityNumber?.value)}
        </Text>
      </TableCell>
    </TableRow>
  );
}
